import {
  buildUrl,
  compactParams,
  deleteData,
  deleteRequest,
  encodeSegment,
  getApiBaseUrl,
  getData,
  postData,
  putData,
} from './api';
import type {
  Account,
  Transaction,
  TransactionAttachment,
  Category,
  BudgetSettings,
  CreateAccountDTO,
  UpdateAccountDTO,
  CreateTransactionDTO,
  UpdateTransactionDTO,
  CreateCategoryDTO,
  UpdateCategoryDTO,
  CreateAttachmentDTO,
  TransactionFilters,
} from '../types/budget';

export interface TransactionWithRelations extends Transaction {
  category: Category | null;
  account: Account | null;
}

export interface BulkOperationResult {
  count: number;
}

export interface CategoryTransactionCount {
  count: number;
}

export interface ShareUser {
  id: string;
  email: string;
  displayName: string | null;
}

export interface BudgetShareOwned {
  id: string;
  createdAt: string;
  sharedWithUser: ShareUser;
}

export interface BudgetShareReceived {
  id: string;
  createdAt: string;
  owner: ShareUser;
}

const buildViewingUserUrl = (path: string, viewingUserId?: string): string => {
  return buildUrl(path, { viewingUserId });
};

const buildTransactionsUrl = (
  filters?: Partial<TransactionFilters>,
  viewingUserId?: string
): string => {
  return buildUrl('/api/budget/transactions', compactParams({
    viewingUserId,
    quickFilter: filters?.quickFilter && filters.quickFilter !== 'all' ? filters.quickFilter : undefined,
    categoryId: filters?.categoryId,
    accountId: filters?.accountId,
    dateFrom: filters?.dateFrom,
    dateTo: filters?.dateTo,
    sortField: filters?.sortField,
    sortDirection: filters?.sortDirection,
  }));
};

export const accountApi = {
  getAll(activeOnly = false, viewingUserId?: string): Promise<Account[]> {
    return getData<Account[]>(buildUrl('/api/budget/accounts', compactParams({
      activeOnly: activeOnly ? true : undefined,
      viewingUserId,
    })));
  },

  getById(id: string): Promise<Account> {
    return getData<Account>(`/api/budget/accounts/${id}`);
  },

  create(data: CreateAccountDTO): Promise<Account> {
    return postData<Account, CreateAccountDTO>('/api/budget/accounts', data);
  },

  update(id: string, data: UpdateAccountDTO): Promise<Account> {
    return putData<Account, UpdateAccountDTO>(`/api/budget/accounts/${id}`, data);
  },

  deactivate(id: string): Promise<Account> {
    return postData<Account>(`/api/budget/accounts/${id}/deactivate`);
  },

  reactivate(id: string): Promise<Account> {
    return postData<Account>(`/api/budget/accounts/${id}/reactivate`);
  },

  delete(id: string): Promise<void> {
    return deleteRequest(`/api/budget/accounts/${id}`);
  },

  reorder(orderedIds: string[]): Promise<Account[]> {
    return putData<Account[], { orderedIds: string[] }>('/api/budget/accounts/reorder', { orderedIds });
  },
};

export const transactionApi = {
  getAll(filters?: Partial<TransactionFilters>, viewingUserId?: string): Promise<TransactionWithRelations[]> {
    return getData<TransactionWithRelations[]>(buildTransactionsUrl(filters, viewingUserId));
  },

  getById(id: string): Promise<TransactionWithRelations> {
    return getData<TransactionWithRelations>(`/api/budget/transactions/${id}`);
  },

  create(data: CreateTransactionDTO): Promise<TransactionWithRelations> {
    return postData<TransactionWithRelations, CreateTransactionDTO>('/api/budget/transactions', data);
  },

  update(id: string, data: UpdateTransactionDTO): Promise<TransactionWithRelations> {
    return putData<TransactionWithRelations, UpdateTransactionDTO>(`/api/budget/transactions/${id}`, data);
  },

  delete(id: string): Promise<void> {
    return deleteRequest(`/api/budget/transactions/${id}`);
  },

  bulkCreate(transactions: CreateTransactionDTO[], accountId?: string): Promise<BulkOperationResult> {
    return postData<BulkOperationResult, { transactions: CreateTransactionDTO[]; accountId?: string }>(
      '/api/budget/transactions/bulk',
      { transactions, accountId }
    );
  },

  bulkUpdate(ids: string[], categoryId?: string | null, accountId?: string | null): Promise<BulkOperationResult> {
    return putData<BulkOperationResult, { ids: string[]; categoryId?: string | null; accountId?: string | null }>(
      '/api/budget/transactions/bulk',
      { ids, categoryId, accountId }
    );
  },

  bulkDelete(ids: string[]): Promise<BulkOperationResult> {
    return deleteData<BulkOperationResult>('/api/budget/transactions/bulk', {
      data: { ids },
    });
  },
};

export const categoryApi = {
  getAll(viewingUserId?: string): Promise<Category[]> {
    return getData<Category[]>(buildViewingUserUrl('/api/budget/categories', viewingUserId));
  },

  getById(id: string): Promise<Category> {
    return getData<Category>(`/api/budget/categories/${id}`);
  },

  create(data: CreateCategoryDTO): Promise<Category> {
    return postData<Category, CreateCategoryDTO>('/api/budget/categories', data);
  },

  update(id: string, data: UpdateCategoryDTO): Promise<Category> {
    return putData<Category, UpdateCategoryDTO>(`/api/budget/categories/${id}`, data);
  },

  delete(id: string): Promise<void> {
    return deleteRequest(`/api/budget/categories/${id}`);
  },

  reorder(orderedIds: string[]): Promise<Category[]> {
    return putData<Category[], { orderedIds: string[] }>('/api/budget/categories/reorder', { orderedIds });
  },

  async getTransactionCount(id: string): Promise<number> {
    return (await getData<CategoryTransactionCount>(`/api/budget/categories/${id}/transaction-count`)).count;
  },

  initializeDefaults(): Promise<Category[]> {
    return postData<Category[]>('/api/budget/categories/initialize');
  },
};

export const settingsApi = {
  get(): Promise<BudgetSettings> {
    return getData<BudgetSettings>('/api/budget/settings');
  },

  update(data: Partial<BudgetSettings>): Promise<BudgetSettings> {
    return putData<BudgetSettings, Partial<BudgetSettings>>('/api/budget/settings', data);
  },
};

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
}

export const attachmentApi = {
  getUploadUrl(filename: string, contentType: string): Promise<UploadUrlResponse> {
    return postData<UploadUrlResponse, { filename: string; contentType: string }>(
      '/api/budget/attachments/upload-url',
      { filename, contentType }
    );
  },

  create(transactionId: string, data: CreateAttachmentDTO): Promise<TransactionAttachment> {
    return postData<TransactionAttachment, CreateAttachmentDTO>(
      `/api/budget/transactions/${transactionId}/attachments`,
      data
    );
  },

  getForTransaction(transactionId: string): Promise<TransactionAttachment[]> {
    return getData<TransactionAttachment[]>(`/api/budget/transactions/${transactionId}/attachments`);
  },

  delete(id: string): Promise<void> {
    return deleteRequest(`/api/budget/attachments/${id}`);
  },

  getViewUrl(s3Key: string): string {
    return `${getApiBaseUrl()}/api/budget/attachments/${encodeSegment(s3Key)}`;
  },

  async uploadAndCreate(transactionId: string, file: File): Promise<TransactionAttachment> {
    const { uploadUrl, s3Key } = await this.getUploadUrl(file.name, file.type);

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
    }

    return this.create(transactionId, {
      s3Key,
      filename: file.name,
      contentType: file.type,
      size: file.size,
    });
  },
};

export const sharingApi = {
  getSharesOwned(): Promise<BudgetShareOwned[]> {
    return getData<BudgetShareOwned[]>('/api/budget/shares/owned');
  },

  getSharesReceived(): Promise<BudgetShareReceived[]> {
    return getData<BudgetShareReceived[]>('/api/budget/shares/received');
  },

  createShare(email: string): Promise<BudgetShareOwned> {
    return postData<BudgetShareOwned, { email: string }>('/api/budget/shares', { email });
  },

  revokeShare(shareId: string): Promise<void> {
    return deleteRequest(`/api/budget/shares/${shareId}`);
  },
};

export const budgetApi = {
  accounts: accountApi,
  transactions: transactionApi,
  categories: categoryApi,
  settings: settingsApi,
  attachments: attachmentApi,
  sharing: sharingApi,
};

export default budgetApi;
